import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faGlobe,
  faRocket,
  faLock,
  faChartLine,
  faDollarSign,
  faBullseye,
  faClipboard,
  faClock,
  faShield,
  faWrench,
  faScrewdriver,
  faBook,
  faGraduationCap,
  faBriefcase,
  faChartBar,
  faBolt,
  faLaptop,
  faUsers,
  faPlane,
  faHandshake,
  faStar,
  faSearch,
  faMobileAlt,
  faFile,
  faThumbsUp,
  faCog,
  faHourglassEnd,
} from "@fortawesome/free-solid-svg-icons";
import NavBar from "../Navigation/NavBar";
import Footer from "../Footer/Footer";
import Services1 from "./IMG/Services1.png";
import Services2 from "./IMG/Services2.png";
import Services3 from "./IMG/Services3.png";
import Services4 from "./IMG/Services4.png";
import Services5 from "./IMG/Services5.png";
import Services6 from "./IMG/Services6.png";
import Services7 from "./IMG/Services7.png";
import Services8 from "./IMG/Services8.png";
import { ReactComponent as StarIcon } from "./IMG/Star.svg";
import { ReactComponent as BoltIcon } from "./IMG/Bolt.svg";
import { ReactComponent as WalletIcon } from "./IMG/Wallet.svg";
import { ReactComponent as ComputerIcon } from "./IMG/Computer.svg";
import { ReactComponent as GlobeIcon } from "./IMG/Globe.svg";
import { ReactComponent as BriefcaseIcon } from "./IMG/Briefcase.svg";
import { ReactComponent as PeopleIcon } from "./IMG/People.svg";
import { ReactComponent as PassportIcon } from "./IMG/Passport.svg";
import "./ServiceDetails.css";

// Helper component for rendering FontAwesome icons
const FAIcon = ({ icon }) => <FontAwesomeIcon icon={icon} />;

const featureIcons = {
  check: faCheck,
  globe: faGlobe,
  rocket: faRocket,
  lock: faLock,
  chart: faChartLine,
  money: faDollarSign,
  target: faBullseye,
  clipboard: faClipboard,
  clock: faClock,
  shield: faShield,
  tools: faWrench,
  wrench: faScrewdriver,
  book: faBook,
  graduation: faGraduationCap,
  briefcase: faBriefcase,
  analytics: faChartBar,
  speed: faBolt,
  plane: faPlane,
  handshake: faHandshake,
  star: faStar,
  search: faSearch,
  mobile: faMobileAlt,
  document: faFile,
  thumbsUp: faThumbsUp,
  laptop: faLaptop,
  users: faUsers,
  gear: faCog,
  hourglassEnd: faHourglassEnd,
};


const serviceDetailsMap = {
  "permanent-staffing": {
    title: "Permanent Staffing Solutions",
    icon: StarIcon,
    badge: "GLOBAL RECRUITMENT",
    image: Services1,
    overview:
      "Quarec Resources is a premier recruitment firm dedicated to connecting talent from around the world with exciting opportunities across national and international job markets.",
    description: [
      "Our extensive reach and expertise allow us to connect skilled professionals with the best job opportunities in regions such as the Middle East, Africa, Canada, UK, Australia, and beyond. With a focus on professional recruiting principles, we ensure a seamless and effective recruitment process that maximizes the potential of our diverse clientele.",
      "Our mission is to help individuals build a safe and secure future by connecting them with international job prospects that offer growth and stability. Join Quarec Resources on an exciting journey towards a brighter future, where global opportunities await talented individuals like you.",
    ],
    features: [
      { icon: featureIcons.globe, title: "Global Reach", desc: "Access to opportunities worldwide" },
      { icon: featureIcons.check, title: "Best Matches", desc: "Perfect job-candidate alignment" },
      { icon: featureIcons.rocket, title: "Career Growth", desc: "Long-term career development" },
      { icon: featureIcons.lock, title: "Secure Future", desc: "Stable employment opportunities" },
    ],
    benefits: [
      "Extensive candidate database",
      "Personalized placement services",
      "Comprehensive onboarding support",
      "Career development assistance",
      "Ongoing mentorship and support",
    ],
    process: [
      { step: 1, title: "Assessment", desc: "Evaluate your skills and requirements" },
      { step: 2, title: "Matching", desc: "Find perfect job opportunities" },
      { step: 3, title: "Placement", desc: "Secure your ideal position" },
      { step: 4, title: "Support", desc: "Ongoing career assistance" },
    ],
  },
  "temporary-staffing": {
    title: "Temporary Staffing",
    icon: BoltIcon,
    badge: "FLEXIBLE STAFFING",
    image: Services2,
    overview:
      "Quarec Resources is a staffing agency specializing in temporary staffing solutions with excellence in sourcing and vetting candidates.",
    description: [
      "We excel at sourcing and vetting candidates for different positions, taking care of administrative tasks such as contracts and payroll. Our temporary workers seamlessly integrate into client teams and contribute to their overall success.",
      "We provide clients with scalability, cost-effectiveness, access to specialized skills, and the chance to evaluate potential permanent hires. Temporary workers benefit from valuable experience, exposure to diverse industries, networking opportunities, and potential pathways to permanent employment.",
    ],
    features: [
      { icon: featureIcons.chart, title: "Scalability", desc: "Adjust workforce as needed" },
      { icon: featureIcons.money, title: "Cost-Effective", desc: "Optimize labor costs" },
      { icon: featureIcons.target, title: "Specialized Skills", desc: "Access expert professionals" },
      { icon: featureIcons.clipboard, title: "Full Compliance", desc: "Legal and regulatory compliance" },
    ],
    benefits: [
      "Quick workforce deployment",
      "Trial hiring opportunities",
      "Administrative task management",
      "Competitive payroll processing",
      "Flexible contract terms",
    ],
    process: [
      { step: 1, title: "Requirement Analysis", desc: "Understand your staffing needs" },
      { step: 2, title: "Candidate Search", desc: "Find qualified professionals" },
      { step: 3, title: "Quick Deployment", desc: "Rapid placement and onboarding" },
      { step: 4, title: "Full Support", desc: "Administrative and compliance management" },
    ],
  },
  "payroll-management": {
    title: "Payroll Management",
    icon: WalletIcon,
    badge: "PAYROLL & HR",
    image: Services3,
    overview:
      "At Quarec Resources, our payroll management ensures accurate and timely compensation for all our workers with meticulous attention to detail.",
    description: [
      "Our dedicated team handles timekeeping, wage calculation, deductions, and payroll processing. We comply with legal requirements, providing detailed payslips and maintaining confidentiality. With efficient processes in place, we prioritize accuracy and transparency.",
      "Our goal is to ensure that our workers receive their wages on time and are fully supported throughout the payroll process. Clear communication and prompt assistance are at the forefront of our payroll management approach.",
    ],
    features: [
      { icon: featureIcons.clock, title: "Timely Processing", desc: "Documented processing and reconciliation" },
      { icon: featureIcons.check, title: "Accuracy", desc: "Error-free calculations" },
      { icon: featureIcons.shield, title: "Legal Compliance", desc: "Full regulatory adherence" },
      { icon: featureIcons.lock, title: "Data Security", desc: "Secure confidential information" },
    ],
    benefits: [
      "Automated payroll processing",
      "Tax compliance management",
      "Multiple payment methods",
      "Detailed reporting and analytics",
      "Employee portal access",
    ],
    process: [
      { step: 1, title: "Data Collection", desc: "Gather timekeeping records" },
      { step: 2, title: "Calculation", desc: "Process wages and deductions" },
      { step: 3, title: "Compliance Check", desc: "Ensure tax and legal compliance" },
      { step: 4, title: "Distribution", desc: "Timely payment processing" },
    ],
  },
  "dedicated-it": {
    title: "Dedicated IT Resources",
    icon: ComputerIcon,
    badge: "IT SOLUTIONS",
    image: Services4,
    overview:
      "Quarec Resources offers dedicated IT resources and teams to support a wide range of projects with specialized expertise.",
    description: [
      "Our skilled IT professionals are available to provide specialized expertise and assistance tailored to your specific needs. Whether you require support for software development, system administration, network infrastructure, cybersecurity, or any other IT-related projects.",
      "Our dedicated resources are ready to contribute their knowledge and skills. We ensure that our IT teams are equipped with the latest technologies and tools to deliver efficient and effective solutions. Partner with Quarec Resources to access dedicated IT resources that will drive the success of your projects.",
    ],
    features: [
      { icon: featureIcons.tools, title: "Technical Expertise", desc: "Certified professionals" },
      { icon: featureIcons.wrench, title: "24/7 Support", desc: "Round-the-clock assistance" },
      { icon: featureIcons.book, title: "Latest Technology", desc: "Modern tech stack" },
      { icon: featureIcons.graduation, title: "Expert Team", desc: "Highly skilled experts" },
    ],
    benefits: [
      "Full-stack development capability",
      "Infrastructure management",
      "Cloud solutions expertise",
      "Cybersecurity support",
      "Continuous technical support",
    ],
    process: [
      { step: 1, title: "Needs Assessment", desc: "Evaluate IT requirements" },
      { step: 2, title: "Team Building", desc: "Assemble expert team" },
      { step: 3, title: "Implementation", desc: "Deploy solutions" },
      { step: 4, title: "Maintenance", desc: "Ongoing support and updates" },
    ],
  },
  "international-recruitment": {
    title: "International Recruitment",
    icon: GlobeIcon,
    badge: "GLOBAL HIRING",
    image: Services5,
    overview:
      "We specialize in international recruitment, assisting companies in sourcing and hiring talented professionals from around the world.",
    description: [
      "We understand the complexities of global hiring and provide comprehensive solutions including visa support, cultural orientation, and relocation logistics. Our process includes candidate screening, skill assessment, travel arrangements, and on-boarding support.",
      "We ensure a smooth transition for international hires while maintaining full compliance with immigration laws. Let us handle the complexities of international recruitment while you focus on growing your business.",
    ],
    features: [
      { icon: featureIcons.clipboard, title: "Visa Support", desc: "Complete visa assistance" },
      { icon: featureIcons.plane, title: "Travel Logistics", desc: "Relocation arrangements" },
      { icon: featureIcons.target, title: "Skill Screening", desc: "Expert candidate vetting" },
      { icon: featureIcons.handshake, title: "Cultural Support", desc: "Integration assistance" },
    ],
    benefits: [
      "Multi-country sourcing",
      "Visa and immigration expertise",
      "Travel and accommodation",
      "Cultural preparation programs",
      "Regulatory compliance",
    ],
    process: [
      { step: 1, title: "Global Search", desc: "Source international talent" },
      { step: 2, title: "Visa Processing", desc: "Handle documentation" },
      { step: 3, title: "Logistics", desc: "Arrange travel and accommodation" },
      { step: 4, title: "Integration", desc: "Onboard and support" },
    ],
  },
  "executive-search": {
    title: "Executive Recruitment",
    icon: BriefcaseIcon,
    badge: "EXECUTIVE SEARCH",
    image: Services6,
    overview:
      "Our executive search and recruitment services identify top-tier leadership talent for your organization.",
    description: [
      "We understand that finding the right executive is critical for organizational success and utilize our extensive network of industry leaders and professionals. We conduct thorough vetting processes including background checks, skills assessments, and cultural fit analysis.",
      "Our consultants work closely with your leadership team to ensure that candidates align with your strategic vision. We provide personalized support throughout the entire journey from assessment to negotiation and onboarding.",
    ],
    features: [
      { icon: featureIcons.star, title: "Top Talent", desc: "Access elite professionals" },
      { icon: featureIcons.search, title: "Deep Vetting", desc: "Thorough background checks" },
      { icon: featureIcons.handshake, title: "Cultural Fit", desc: "Value alignment" },
      { icon: featureIcons.mobile, title: "Direct Contact", desc: "Personalized service" },
    ],
    benefits: [
      "Executive-level talent pool",
      "Industry-specific expertise",
      "Market analysis and insights",
      "Negotiation support",
      "Post-placement support",
    ],
    process: [
      { step: 1, title: "Requirement Definition", desc: "Understand leadership needs" },
      { step: 2, title: "Candidate Search", desc: "Industry-specific recruitment" },
      { step: 3, title: "Vetting", desc: "In-depth assessment" },
      { step: 4, title: "Placement & Support", desc: "Onboard and ensure success" },
    ],
  },
  "bulk-hiring": {
    title: "Bulk Hiring Services",
    icon: PeopleIcon,
    badge: "MASS RECRUITMENT",
    image: Services7,
    overview:
      "For organizations looking to expand their workforce rapidly, our bulk hiring services provide comprehensive recruitment solutions.",
    description: [
      "We handle large-scale recruitment projects with efficiency, managing everything from candidate sourcing to onboarding with precision and speed. Our team understands the complexities of hiring in volume while maintaining quality standards.",
      "We leverage our extensive network and advanced screening processes to deliver qualified candidates that meet your specific requirements and company culture, all within your timeline.",
    ],
    features: [
      { icon: featureIcons.gear, title: "Streamlined Process", desc: "Efficient workflows" },
      { icon: featureIcons.speed, title: "Quick Turnaround", desc: "Fast deployment" },
      { icon: featureIcons.target, title: "Quality Assurance", desc: "Consistent standards" },
      { icon: featureIcons.analytics, title: "Analytics", desc: "Real-time reporting" },
    ],
    benefits: [
      "Large-scale candidate database",
      "Automated screening systems",
      "Batch recruitment capabilities",
      "Simplified administration",
      "Cost optimization",
    ],
    process: [
      { step: 1, title: "Volume Planning", desc: "Define hiring strategy" },
      { step: 2, title: "Rapid Sourcing", desc: "Large-scale recruitment" },
      { step: 3, title: "Efficient Screening", desc: "Streamlined selection" },
      { step: 4, title: "Quick Deployment", desc: "Batch onboarding" },
    ],
  },
  "visa-assistance": {
    title: "Visa Assistance",
    icon: PassportIcon,
    badge: "VISA SUPPORT",
    image: Services8,
    overview:
      "We specialize in fast and reliable visa services with extensive experience processing travel documents.",
    description: [
      "We understand the complexity of immigration processes and specialize in quick, accurate visa services. Visa applications require accurate documents and checks against current consular requirements; processing times and decisions remain with the relevant authorities.",
      "We handle all documentation requirements, expedited processing, and consulate formalities. Upon receiving visa documents, we ensure all necessary documents are submitted and tracked through the entire process.",
    ],
    features: [
      { icon: featureIcons.document, title: "Documentation", desc: "Complete document handling" },
      { icon: featureIcons.speed, title: "Fast Processing", desc: "Expedited services" },
      { icon: featureIcons.thumbsUp, title: "Expert Guidance", desc: "Professional assistance" },
      { icon: featureIcons.check, title: "Document checks", desc: "Review of requirements and supporting documents" },
    ],
    benefits: [
      "Multiple visa type expertise",
      "Rapid processing times",
      "Consulate relationships",
      "Document management",
      "Follow-up support",
    ],
    process: [
      { step: 1, title: "Consultation", desc: "Discuss visa requirements" },
      { step: 2, title: "Documentation", desc: "Prepare required documents" },
      { step: 3, title: "Submission", desc: "File with consulate" },
      { step: 4, title: "Tracking", desc: "Monitor and follow up" },
    ],
  },
};

const ServiceDetails = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const serviceData = serviceDetailsMap[serviceId];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (!serviceData) {
    return (
      <div style={{ paddingTop: "100px", textAlign: "center" }}>
        <h1>Service not found</h1>
      </div>
    );
  }

  return (
    <>
      <NavBar />

      {/* Back Button */}
      <button className="service-back-btn" onClick={() => navigate("/services-gallery")}>
        ← Back to Services
      </button>

      {/* Hero Section */}
      <div className="service-hero">
        <div className="service-hero-overlay"></div>
        <img src={serviceData.image} alt={serviceData.title} className="service-hero-image" />
        <div className="service-hero-content">
          <div className="service-hero-badge">{serviceData.badge}</div>
          <h1 className="service-hero-title">{serviceData.title}</h1>
          <p className="service-hero-subtitle">{serviceData.overview}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="service-details-container">
        {/* Description Section */}
        <section className="service-section">
          <div className="service-section-content">
            <h2 className="service-section-title">Overview</h2>
            <div className="service-description-text">
              {serviceData.description.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="service-section">
          <div className="service-section-content">
            <h2 className="service-section-title">Key Features</h2>
            <div className="service-features-grid">
              {serviceData.features.map((feature, idx) => (
                <div key={idx} className="service-feature-card">
                  <div className="service-feature-icon">
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  <h3 className="service-feature-title">{feature.title}</h3>
                  <p className="service-feature-desc">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="service-section service-section-alt">
          <div className="service-section-content">
            <h2 className="service-section-title">Benefits</h2>
            <div className="service-benefits-list">
              {serviceData.benefits.map((benefit, idx) => (
                <div key={idx} className="service-benefit-item">
                  <div className="service-benefit-icon">
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <div className="service-benefit-text">{benefit}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="service-section">
          <div className="service-section-content">
            <h2 className="service-section-title">How It Works</h2>
            <div className="service-process">
              {serviceData.process.map((item, idx) => (
                <div key={idx} className="service-process-step">
                  <div className="service-process-number">{item.step}</div>
                  <div className="service-process-content">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                  {idx < serviceData.process.length - 1 && (
                    <div className="service-process-arrow">→</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta-section">
          <div className="service-cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Contact our team today to discuss how we can support your needs</p>
            <button className="service-cta-button">Get in Touch</button>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ServiceDetails;
